import { get, isNumber, map } from 'lodash/fp';
import { createSelector } from 'reselect';
import { LEARN_TYPES } from 'constants/config';
import { contentsByProgressSelector } from './contents';
import {
  createCompoundId,
  getAlgoliaIdFieldForContent,
  getNormalizedContentIdentifiersForSearchHit,
} from './helpers';

const hitSelector = (state, hit) => hit;
const normalizedDataSelector = state => state.data.normalized;
const selectedTopicIdSelector = state => state.search.filters.topic.id;
const selectedProgressSelector = state => state.search.filters.progress;

export const createNormalizedContentDataSelector = () =>
  createSelector(hitSelector, normalizedDataSelector, (hit, normalizedData) => {
    const { compoundId } = getNormalizedContentIdentifiersForSearchHit(hit);
    return get(compoundId, normalizedData);
  });

/**
 * Takes a content object and returns an Algolia numeric filter value in the
 * same format that we store in the index. Examples of the id field stored in
 * Algolia are: l_id, p_id, t_id, etc.
 *
 * @example
 * // returns 'l_id=2'
 * getAlgoliaNumericFilterValueForContent({ id: 2, type: 'lesson' });
 *
 * @param {object} content The content object id
 * @return {string} The id of the content in the expected Algolia numeric filter
 * format.
 */
const getAlgoliaNumericFilterValueForContent = content => {
  const idField = getAlgoliaIdFieldForContent(content);
  return `${idField}=${content.id}`;
};

export const getNumericFilterValuesForSelectedTopic = createSelector(
  selectedTopicIdSelector,
  normalizedDataSelector,
  (selectedTopicId, normalizedData) => {
    const id = parseInt(selectedTopicId, 10);
    if (!isNumber(id)) {
      return [];
    }
    const compoundId = createCompoundId({ id, type: LEARN_TYPES.TAG });
    const contents = get([compoundId, 'contents'], normalizedData) || [];
    return contents.map(getAlgoliaNumericFilterValueForContent);
  },
);

export const getContentsForSelectedProgress = createSelector(
  selectedProgressSelector,
  contentsByProgressSelector,
  (selectedProgress, contents) => {
    switch (selectedProgress) {
      case 'assigned':
        return contents.assigned;
      case 'completed':
        return contents.completed;
      case 'viewed':
        return contents.started;
      default:
        return [];
    }
  },
);

export const getNumericFilterValuesForSelectedProgress = createSelector(
  getContentsForSelectedProgress,
  map(getAlgoliaNumericFilterValueForContent),
);
